import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Checkbox,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import LanRoundedIcon from "@mui/icons-material/LanRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import { motion } from "framer-motion";
import { t } from "../i18n";
import NewAdminPanelPage from "./NewAdminPanelPage";
import {
  destructiveButtonSx,
  dialogSx as baseDialogSx,
  fieldSx as baseFieldSx,
  panelSx,
  primaryButtonSx,
  secondaryButtonSx,
  sectionTitleSx,
} from "../uiStyles";

const dialogSx = {
  ...baseDialogSx,
  "& .MuiDialog-paper": {
    ...baseDialogSx["& .MuiDialog-paper"],
    width: "min(640px, calc(100vw - 32px))",
  },
};

const EVENT_PERIODS = [
  { value: 24, key: "shell.last24Hours" },
  { value: 24 * 7, key: "shell.last7Days" },
  { value: 24 * 30, key: "shell.last30Days" },
];

const ANALYTICS_PERIODS = [
  { value: 1, ru: "1 час", en: "1 hour" },
  { value: 3, ru: "3 часа", en: "3 hours" },
  { value: 9, ru: "9 часов", en: "9 hours" },
  { value: 24, ru: "1 день", en: "1 day" },
  { value: 72, ru: "3 дня", en: "3 days" },
  { value: 168, ru: "7 дней", en: "7 days" },
  { value: "all", ru: "Весь период", en: "Entire period" },
];

function AnalyticsPeriodSelect({ value = "all", currentLocale, onChange, disabled = false }) {
  return <TextField select size="small" label={currentLocale === "en" ? "Period" : "Период"} value={value} disabled={disabled} onChange={(event) => onChange?.(event.target.value)} sx={{ minWidth: { xs: "100%", sm: 170 }, "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}>
    {ANALYTICS_PERIODS.map((item) => <MenuItem key={item.value} value={item.value}>{item[currentLocale === "en" ? "en" : "ru"]}</MenuItem>)}
  </TextField>;
}

function analyticsPeriodLabel(value, currentLocale, retentionDays = 7) {
  if (String(value) === "all") return currentLocale === "en" ? `Entire period (${retentionDays} days)` : `Весь период (${retentionDays} дней)`;
  const item = ANALYTICS_PERIODS.find((period) => String(period.value) === String(value));
  return item?.[currentLocale === "en" ? "en" : "ru"] || "";
}

function themedPrimaryButtonSx(isDark) {
  return isDark
    ? {
        ...primaryButtonSx,
        bgcolor: "#7a746b",
        "&:hover": { bgcolor: "#625d56", boxShadow: "none" },
        "&.Mui-disabled": { bgcolor: "rgba(122,116,107,0.42)", color: "rgba(255,255,255,0.82)", boxShadow: "none" },
      }
    : primaryButtonSx;
}

function themedSecondaryButtonSx(isDark) {
  return isDark ? { ...secondaryButtonSx, color: "rgba(226,232,240,0.88)" } : secondaryButtonSx;
}

function countryFlagEmoji(countryCode) {
  const raw = String(countryCode || "").trim();
  const code = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(code)) {
    return String.fromCodePoint(...code.split("").map((char) => 127397 + char.charCodeAt(0)));
  }
  const fallback = {
    RU: "🇷🇺",
    US: "🇺🇸",
    DE: "🇩🇪",
    FR: "🇫🇷",
    NL: "🇳🇱",
    GB: "🇬🇧",
    UK: "🇬🇧",
    TR: "🇹🇷",
    KZ: "🇰🇿",
    BY: "🇧🇾",
    UA: "🇺🇦",
    PL: "🇵🇱",
  };
  return fallback[code] || "📍";
}

function ActionIcon({ title, onClick, color = "default", children, currentTheme = "light" }) {
  const isDark = currentTheme === "dark";
  const tone = color === "success"
    ? { border: "rgba(22,163,74,0.24)", color: "success.main", background: "rgba(22,163,74,0.08)" }
    : color === "warning"
      ? { border: "rgba(217,119,6,0.24)", color: "warning.main", background: "rgba(217,119,6,0.08)" }
      : null;
  const sx = color === "danger"
    ? {
        border: "1px solid rgba(220,38,38,0.16)",
        borderRadius: 999,
        color: "error.main",
        width: 42,
        height: 42,
        bgcolor: isDark ? "rgba(220,38,38,0.08)" : "rgba(255,255,255,0.96)",
      }
    : {
        border: `1px solid ${tone?.border || (isDark ? "rgba(148,163,184,0.22)" : "rgba(17,24,39,0.12)")}`,
        borderRadius: 999,
        color: tone?.color || "text.primary",
        width: 42,
        height: 42,
        bgcolor: tone?.background || (isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.96)"),
      };
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton onClick={onClick} sx={sx}>{children}</IconButton>
      </span>
    </Tooltip>
  );
}

function MetaPill({ icon, label, value }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.55, maxWidth: "100%" }}>
      <Box sx={{ color: "text.secondary", lineHeight: 0, display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography variant="body2" sx={{ lineHeight: 1.2, wordBreak: "break-word" }}>
        <Box component="span" sx={{ color: "text.secondary" }}>{label}: </Box>
        <Box component="span">{value}</Box>
      </Typography>
    </Box>
  );
}

function CopyableIp({ ip, currentLocale, onCopied }) {
  const copy = async (event) => {
    event.stopPropagation();
    if (!ip) return;
    await navigator.clipboard.writeText(ip);
    onCopied?.(ip);
  };
  if (!ip) return <Box component="span">-</Box>;
  return <Tooltip title={currentLocale === "en" ? "Copy" : "Скопировать"} arrow>
    <Box component="span" role="button" tabIndex={0} aria-label={currentLocale === "en" ? "Copy IP address" : "Скопировать IP-адрес"} onClick={copy} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") copy(event); }} sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, cursor: "default", borderRadius: 1, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 } }}>
      <Box component="span">{ip}</Box>
      <ContentCopyRoundedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
    </Box>
  </Tooltip>;
}

function formatCompactBitrate(value) {
  const numeric = Number(value) || 0;
  if (numeric <= 0) return "0 bit/s";
  if (numeric >= 1000 * 1000 * 1000) return `${(numeric / (1000 * 1000 * 1000)).toFixed(numeric >= 1000 * 1000 * 10000 ? 0 : 1)} Gbit/s`;
  if (numeric >= 1000 * 1000) return `${(numeric / (1000 * 1000)).toFixed(numeric >= 1000 * 1000 * 10 ? 1 : 2)} Mbit/s`;
  if (numeric >= 1000) return `${(numeric / 1000).toFixed(numeric >= 1000 * 10 ? 1 : 2)} Kbit/s`;
  return `${Math.round(numeric)} bit/s`;
}

function averageBitrateForPeriod(totalBytes, periodHours) {
  const seconds = Math.max(1, (Number(periodHours) || 0) * 60 * 60);
  return (Number(totalBytes) || 0) * 8 / seconds;
}

function renderLocation(item) {
  const city = String(item?.city || "").trim();
  const rawCode = String(item?.countryCode || "").trim().toUpperCase();
  const flag = rawCode ? countryFlagEmoji(rawCode) : "";
  if (city) {
    if (flag && flag !== "📍" && flag !== rawCode) return `${flag} ${city}`;
    return city;
  }
  if (flag && flag !== "📍" && flag !== rawCode) return flag;
  return "";
}

function getTrafficTone(profile, totals, isDark) {
  if (!profile?.online) {
    return {
      bg: isDark ? "rgba(120,113,108,0.16)" : "rgba(107,114,128,0.06)",
      border: isDark ? "rgba(168,162,158,0.28)" : "rgba(17,24,39,0.08)",
      color: isDark ? "#d6d3d1" : "#6b7280",
    };
  }
  const profileRate = Number(profile?.totalRateBps) || 0;
  const peakRate = Math.max(1, Number(profile?.peakTotalRateBps) || Number(totals?.peakTotalRateBps) || profileRate || 1);
  const ratio = profileRate / peakRate;
  if (ratio >= 0.75) return { bg: isDark ? "rgba(239,68,68,0.16)" : "rgba(254,226,226,0.92)", border: "rgba(239,68,68,0.28)", color: isDark ? "#fca5a5" : "#b91c1c" };
  if (ratio > 0.5) return { bg: isDark ? "rgba(245,158,11,0.16)" : "rgba(254,243,199,0.95)", border: "rgba(245,158,11,0.3)", color: isDark ? "#fcd34d" : "#b45309" };
  return { bg: isDark ? "rgba(34,197,94,0.16)" : "rgba(220,252,231,0.95)", border: "rgba(34,197,94,0.28)", color: isDark ? "#86efac" : "#15803d" };
}

function ProfileDialog({ open, title, currentLocale, currentTheme, form, onClose, onChange, onSave, loading, isCreate = false }) {
  const isDark = currentTheme === "dark";
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" sx={dialogSx}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75, mb: 2 }}>{t("shell.profileCreateSubtitle", currentLocale)}</Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 0.5 }}>
        <TextField label={t("shell.humanName", currentLocale)} value={form.displayName || ""} onChange={(e) => onChange("displayName", e.target.value)} fullWidth margin="normal" sx={{ ...baseFieldSx(isDark), mt: 1.5 }} />
        <TextField label={currentLocale === "en" ? "Email" : "Электронная почта"} type="email" value={form.email || ""} onChange={(e) => onChange("email", e.target.value)} fullWidth sx={baseFieldSx(isDark)} />
        <TextField label={t("shell.profileName", currentLocale)} value={form.profileName || ""} disabled fullWidth sx={baseFieldSx(isDark)} />
        {isCreate ? (
          <FormControlLabel
            control={<Checkbox checked={!!form.sendEmail} onChange={(e) => onChange("sendEmail", e.target.checked)} />}
            label={currentLocale === "en" ? "Email the profile link after creation" : "Отправить ссылку на профиль по электронной почте"}
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.25, width: "100%" }}>
          <Button onClick={onClose} disabled={loading} sx={themedSecondaryButtonSx(isDark)}>{t("common.cancel", currentLocale)}</Button>
          <Button variant="contained" onClick={onSave} disabled={loading || (isCreate && (!String(form.displayName || "").trim() || !String(form.profileName || "").trim() || (!!form.sendEmail && !String(form.email || "").trim())))} sx={themedPrimaryButtonSx(isDark)}>{t("common.save", currentLocale)}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function ProtocolChoice({ value, onChange, currentLocale, currentTheme }) {
  const isDark = currentTheme === "dark";
  const isAmnezia = value === "amnezia";
  const description = isAmnezia
    ? (currentLocale === "en" ? "Traffic masking is enabled. Use the AmneziaVPN or AmneziaWG app to import this profile." : "Маскировка трафика включена. Для подключения используйте приложение AmneziaVPN или AmneziaWG.")
    : (currentLocale === "en" ? "Standard WireGuard without masking. Compatible with official WireGuard clients and third-party integrations." : "Стандартный WireGuard без маскировки. Совместим с официальными клиентами WireGuard и сторонними интеграциями.");
  return <Box>
    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>{currentLocale === "en" ? "Connection protocol" : "Протокол подключения"}</Typography>
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75, p: 0.6, borderRadius: 999, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(17,24,39,0.055)", border: "1px solid", borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(17,24,39,0.07)" }}>
      {[{ value: "amnezia", label: "AmneziaWG" }, { value: "wireguard", label: "WireGuard" }].map((option) => {
        const active = value === option.value;
        return <Button key={option.value} onClick={() => onChange(option.value)} disableElevation sx={{ minHeight: 42, borderRadius: 999, textTransform: "none", fontWeight: active ? 700 : 500, color: active ? (isDark ? "#111827" : "#fff") : "text.secondary", bgcolor: active ? (isDark ? "#f8fafc" : "#565d66") : "transparent", boxShadow: active ? (isDark ? "0 4px 14px rgba(0,0,0,0.24)" : "0 4px 14px rgba(17,24,39,0.16)") : "none", "&:hover": { bgcolor: active ? (isDark ? "#fff" : "#454b54") : (isDark ? "rgba(255,255,255,0.07)" : "rgba(17,24,39,0.06)") } }}>{option.label}</Button>;
      })}
    </Box>
    <Box sx={{ mt: 1.5, p: 1.5, minHeight: 78, display: "flex", gap: 1.25, alignItems: "flex-start", borderRadius: 3, bgcolor: isDark ? "rgba(255,255,255,0.035)" : "rgba(17,24,39,0.025)", border: "1px solid", borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(17,24,39,0.07)" }}>
      <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(17,24,39,0.06)", color: "text.primary" }}>{isAmnezia ? <PublicRoundedIcon sx={{ fontSize: 19 }} /> : <LanRoundedIcon sx={{ fontSize: 19 }} />}</Box>
      <Box><Typography sx={{ fontWeight: 700, fontSize: 14 }}>{isAmnezia ? (currentLocale === "en" ? "With traffic masking" : "С маскировкой трафика") : (currentLocale === "en" ? "Maximum compatibility" : "Максимальная совместимость")}</Typography><Typography variant="body2" sx={{ color: "text.secondary", mt: 0.35, lineHeight: 1.45 }}>{description}</Typography></Box>
    </Box>
  </Box>;
}

function DownloadProfileDialog({ open, currentLocale, currentTheme, profileName, configType, onConfigTypeChange, onClose, onDownload, loading }) {
  const isDark = currentTheme === "dark";
  return <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" sx={dialogSx}>
    <DialogTitle><Typography variant="h5" sx={{ fontWeight: 700 }}>{currentLocale === "en" ? "Download connection profile" : "Скачать профиль подключения"}</Typography><Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>{currentLocale === "en" ? "Choose the protocol supported by the client device." : "Выберите протокол, который поддерживает устройство клиента."}</Typography></DialogTitle>
    <DialogContent sx={{ pt: 1 }}>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>{profileName}</Typography>
      <ProtocolChoice value={configType} onChange={onConfigTypeChange} currentLocale={currentLocale} currentTheme={currentTheme} />
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
      <Button onClick={onClose} disabled={loading} sx={themedSecondaryButtonSx(isDark)}>{t("common.cancel", currentLocale)}</Button>
      <Button startIcon={<DownloadRoundedIcon />} variant="contained" onClick={onDownload} disabled={loading} sx={themedPrimaryButtonSx(isDark)}>{currentLocale === "en" ? "Download" : "Скачать"}</Button>
    </DialogActions>
  </Dialog>;
}

function ShareProfileDialog({ open, currentLocale, currentTheme, profile, configType, onConfigTypeChange, onClose, onCopy, onSendEmail, loading }) {
  const isDark = currentTheme === "dark";
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" sx={dialogSx}>
      <DialogTitle>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{t("shell.shareProfile", currentLocale)}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>{currentLocale === "en" ? "Choose which connection protocol the shared link will provide." : "Выберите протокол подключения, который будет доступен по ссылке."}</Typography>
        {profile?.email ? <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>{profile.email}</Typography> : null}
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 1.25, pt: 1 }}>
        <ProtocolChoice value={configType} onChange={onConfigTypeChange} currentLocale={currentLocale} currentTheme={currentTheme} />
        <Button startIcon={<ContentCopyRoundedIcon />} variant="outlined" onClick={() => onCopy(configType)} disabled={loading} sx={themedSecondaryButtonSx(isDark)}>
          {currentLocale === "en" ? "Copy link" : "Скопировать ссылку"}
        </Button>
        <Button startIcon={<EmailRoundedIcon />} variant="contained" onClick={() => onSendEmail(configType)} disabled={loading || !profile?.email} sx={themedPrimaryButtonSx(isDark)}>
          {currentLocale === "en" ? "Send by email" : "Отправить письмо"}
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading}>{t("common.cancel", currentLocale)}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteProfileDialog({ open, currentLocale, currentTheme, profileName, onClose, onConfirm, loading }) {
  const isDark = currentTheme === "dark";
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" sx={dialogSx}>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{t("shell.deleteProfile", currentLocale)}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>{t("shell.deleteProfileText", currentLocale)}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ px: 1.75, py: 1.4, borderRadius: 3, bgcolor: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.12)" }}>
          <Typography sx={{ mb: 0.5, fontWeight: 600 }}>{t("shell.deleteProfileConfirm", currentLocale)}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", wordBreak: "break-word" }}>{profileName}</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.25, width: "100%" }}>
          <Button onClick={onClose} disabled={loading} sx={themedSecondaryButtonSx(isDark)}>{t("common.cancel", currentLocale)}</Button>
          <Button onClick={onConfirm} variant="contained" disabled={loading} sx={destructiveButtonSx}>{t("shell.delete", currentLocale)}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function ProfileEventsDialog({ open, currentLocale, currentTheme, profileName, items = [], periodHours = 24, onClose, onPeriodChange }) {
  const isDark = currentTheme === "dark";
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={dialogSx}>
      <DialogTitle sx={{ pb: 0.75 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{t("shell.connectionEvents", currentLocale)}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>{items[0]?.displayName || profileName}</Typography>
          </Box>
          <TextField
            select
            size="small"
            label={t("shell.period", currentLocale)}
            value={periodHours}
            onChange={(event) => onPeriodChange?.(Number(event.target.value))}
            sx={{ minWidth: 190, ...baseFieldSx(isDark) }}
          >
            {EVENT_PERIODS.map((item) => (
              <MenuItem key={item.value} value={item.value}>{t(item.key, currentLocale)}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={1.15}>
          {items.length === 0 ? (
            <Box sx={{ px: 1.5, py: 1.2, borderRadius: 2.5, border: "1px solid rgba(17,24,39,0.08)", bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(107,114,128,0.04)" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("shell.noEvents", currentLocale)}</Typography>
            </Box>
          ) : items.map((item) => (
            <Box key={item.id || `${item.createdAt}-${item.externalIp}`} sx={{ px: 1.5, py: 1.2, borderRadius: 2.5, border: "1px solid rgba(17,24,39,0.08)", bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(107,114,128,0.04)" }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                  <Chip size="small" color={item.online ? "success" : "default"} label={item.online ? t("shell.statusOnline", currentLocale) : t("shell.statusOffline", currentLocale)} />
                  {item.displayName ? <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>{item.displayName}</Typography> : null}
                  {item.externalIp ? <Typography variant="body2" sx={{ color: "text.secondary" }}>{item.externalIp}</Typography> : null}
                  {renderLocation(item) ? <Typography variant="body2" sx={{ color: "text.secondary" }}>{renderLocation(item)}</Typography> : null}
                </Stack>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>{item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}</Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button variant="contained" onClick={onClose} sx={themedPrimaryButtonSx(isDark)}>{t("common.close", currentLocale) || (currentLocale === "en" ? "Close" : "Закрыть")}</Button>
      </DialogActions>
    </Dialog>
  );
}

function formatBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

const TRAFFIC_COLORS = ["#6366f1", "#06b6d4", "#22c55e", "#f59e0b", "#ec4899", "#8b5cf6", "#14b8a6", "#f97316"];

function TrafficOverview({ data, currentLocale, onOpenProfile, onPeriodChange }) {
  const profiles = (data?.profiles || []).filter((item) => item.totalBytes > 0);
  const leaders = profiles.slice(0, 4);
  const total = Number(data?.totalBytes || 0);
  const leadersTotal = leaders.reduce((sum, item) => sum + Number(item.totalBytes || 0), 0);
  const chartItems = leadersTotal < total ? [...leaders, { profileName: "__other__", displayName: currentLocale === "en" ? "Others" : "Остальные", totalBytes: total - leadersTotal }] : leaders;
  let cursor = 0;
  const slices = chartItems.map((item, index) => {
    const share = (Number(item.totalBytes) / Math.max(total, 1)) * 100;
    const slice = { ...item, share, offset: cursor, color: TRAFFIC_COLORS[index % TRAFFIC_COLORS.length] };
    cursor += share;
    return slice;
  });
  const averageBitrate = averageBitrateForPeriod(total, data?.periodHours);
  return <Box component={motion.div} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.38, ease: "easeOut" }}>
    <Box sx={{ mb: { xs: 2.25, sm: 2.75 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "stretch", sm: "flex-start" }, gap: 1.5, flexDirection: { xs: "column", sm: "row" } }}>
        <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{currentLocale === "en" ? "Traffic usage" : "Использование трафика"}</Typography>
        <Typography variant="body2" color="text.secondary">{currentLocale === "en" ? "Distribution of total data volume between profiles" : "Распределение общего объёма данных между профилями"}</Typography>
        </Box>
        <AnalyticsPeriodSelect value={data?.periodHours === undefined ? "all" : data?.wholePeriod ? "all" : data.periodHours} currentLocale={currentLocale} onChange={onPeriodChange} disabled={!!data?.loading} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2, minmax(150px, 220px))" }, gap: 1, mt: 2 }}>
        {[
          [currentLocale === "en" ? "Total for period" : "Всего за период", formatBytes(total)],
          [currentLocale === "en" ? "Average data rate" : "Средняя скорость передачи", formatCompactBitrate(averageBitrate)],
        ].map(([label, value], index) => <Box component={motion.div} key={label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + index * 0.07, duration: 0.32 }} sx={{ px: { xs: 1.5, sm: 1.75 }, py: 1.25, borderRadius: 2.5, border: "1px solid rgba(17,24,39,0.08)", bgcolor: "background.default", minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.2 }}>{label}</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, sm: 16 }, lineHeight: 1.35 }} noWrap>{value}</Typography>
        </Box>)}
      </Box>
    </Box>
    <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 2.5, md: 4 }} alignItems={{ xs: "stretch", md: "center" }}>
      <Box sx={{ display: "flex", justifyContent: "center", flexShrink: 0 }}>
        <Box sx={{ width: { xs: 168, sm: 184 }, height: { xs: 168, sm: 184 }, position: "relative" }}>
          <Box component="svg" viewBox="0 0 200 200" sx={{ width: "100%", height: "100%", transform: "rotate(-90deg)", overflow: "visible", shapeRendering: "geometricPrecision" }}>
            <circle cx="100" cy="100" r="76" fill="none" stroke="currentColor" strokeWidth="29" sx={{ color: "action.hover" }} />
            {slices.map((slice, index) => {
              const visibleShare = Math.max(slice.share - 0.7, 0.15);
              return <Tooltip key={slice.profileName} arrow placement="top" title={`${slice.displayName || slice.profileName}: ${formatBytes(slice.totalBytes)} · ${slice.share.toFixed(1)}%`}><motion.circle cx="100" cy="100" r="76" pathLength="100" fill="none" stroke={slice.color} strokeWidth="28" strokeLinecap="round" strokeLinejoin="round" strokeDashoffset={-slice.offset - 0.35} initial={{ opacity: 0, strokeDasharray: "0 100" }} animate={{ opacity: 1, strokeDasharray: `${visibleShare} ${100 - visibleShare}` }} whileHover={{ strokeWidth: 32, filter: "brightness(1.08)" }} transition={{ delay: 0.12 + index * 0.08, duration: 0.55, ease: "easeOut", strokeWidth: { duration: 0.18 }, filter: { duration: 0.18 } }} style={{ cursor: slice.profileName === "__other__" ? "default" : "pointer" }} onClick={() => slice.profileName !== "__other__" && onOpenProfile?.(slice)} /></Tooltip>;
            })}
          </Box>
          <Box sx={{ position: "absolute", inset: 38, borderRadius: "50%", bgcolor: "background.paper", display: "grid", placeItems: "center", textAlign: "center", p: 0.5 }}><Box><Typography variant="caption" color="text.secondary">{currentLocale === "en" ? "Total" : "Всего"}</Typography><Typography sx={{ fontWeight: 750, fontSize: { xs: 18, sm: 20 } }}>{formatBytes(total)}</Typography><Typography variant="caption" color="text.secondary">{analyticsPeriodLabel(data?.wholePeriod ? "all" : data?.periodHours, currentLocale, data?.retentionDays)}</Typography></Box></Box>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!data?.enabled ? <Typography color="text.secondary">{currentLocale === "en" ? "Traffic analytics is disabled in administration settings." : "Аналитика трафика выключена в настройках администрирования."}</Typography> : !profiles.length ? <Typography color="text.secondary">{currentLocale === "en" ? "Traffic data will appear after measurements are collected." : "Данные появятся после накопления замеров трафика."}</Typography> : <Stack spacing={0.85}>{chartItems.map((item, index) => {
          const share = total ? (Number(item.totalBytes) / total) * 100 : 0;
          return <Box component={motion.div} key={item.profileName} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.16 + index * 0.06, duration: 0.3 }} onClick={() => item.profileName !== "__other__" && onOpenProfile?.(item)} sx={{ cursor: item.profileName === "__other__" ? "default" : "pointer", display: "grid", gridTemplateColumns: { xs: "1fr auto", sm: "minmax(130px, 1fr) 2fr auto" }, gap: 1, alignItems: "center", py: 0.35 }}>
            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}><Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: TRAFFIC_COLORS[index % TRAFFIC_COLORS.length], flexShrink: 0 }} /><Typography variant="body2" noWrap>{item.displayName || item.profileName}</Typography></Stack>
            <Box sx={{ display: { xs: "none", sm: "block" }, height: 7, bgcolor: "action.hover", borderRadius: 99, overflow: "hidden" }}><Box component={motion.div} initial={{ width: 0 }} animate={{ width: `${Math.max(share, 0.5)}%` }} whileHover={{ scaleX: 1.045, filter: "brightness(1.12) saturate(1.08)" }} transition={{ delay: 0.2 + index * 0.06, duration: 0.55, ease: "easeOut", scaleX: { duration: 0.22, ease: "easeOut" }, filter: { duration: 0.22 } }} sx={{ height: "100%", bgcolor: TRAFFIC_COLORS[index % TRAFFIC_COLORS.length], borderRadius: 99, transformOrigin: "left center", boxShadow: `0 0 0 1px ${TRAFFIC_COLORS[index % TRAFFIC_COLORS.length]}18` }} /></Box>
            <Typography variant="body2" sx={{ fontWeight: 650, whiteSpace: "nowrap" }}>{formatBytes(item.totalBytes)} · {share.toFixed(1)}%</Typography>
          </Box>;
        })}</Stack>}
      </Box>
    </Stack>
  </Box>;
}

function ProfileAnalyticsDialog({ value, currentLocale, currentTheme = "light", onClose, onPeriodChange }) {
  const data = value?.data;
  const traffic = [...(data?.traffic || [])]
    .filter((item) => Number.isFinite(new Date(item?.at).getTime()))
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const maxTraffic = Math.max(1, ...traffic.map((item) => Number(item.rxBytes || 0) + Number(item.txBytes || 0)));
  // WireGuard counters are from the server's perspective: TX to a peer is a
  // user's download, while RX from a peer is a user's upload.
  const downloadedTotal = Number(data?.txBytes || 0);
  const uploadedTotal = Number(data?.rxBytes || 0);
  const totalTraffic = Number(data?.totalBytes || downloadedTotal + uploadedTotal);
  const averageBitrate = averageBitrateForPeriod(totalTraffic, data?.periodHours);
  const firstTrafficAt = traffic.length ? new Date(traffic[0].at).getTime() : 0;
  const lastTrafficAt = traffic.length ? new Date(traffic[traffic.length - 1].at).getTime() : 0;
  const trafficSpanMs = Math.max(0, lastTrafficAt - firstTrafficAt);
  const formatAxisTime = (timestamp) => new Date(timestamp).toLocaleString([], trafficSpanMs <= 24 * 60 * 60 * 1000
    ? { hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  const timeLabels = traffic.length ? [firstTrafficAt, firstTrafficAt + trafficSpanMs / 2, lastTrafficAt] : [];
  const profile = value?.profile || {};
  const transportLabel = (profile.activeTransports || []).map((item) => item === "amnezia" ? "AmneziaWG" : item === "wireguard" ? "WireGuard" : item).join(" + ") || "—";
  const endpointPort = profile.endpoint?.startsWith("[")
    ? profile.endpoint.slice(profile.endpoint.indexOf("]") + 1).replace(/^:/, "")
    : (profile.endpoint?.includes(":") ? profile.endpoint.split(":").pop() : "");
  const handshakeLabel = profile.lastSeenAt ? new Date(profile.lastSeenAt).toLocaleString() : "—";
  return <Dialog open={!!value?.open} onClose={onClose} fullWidth maxWidth="md" sx={baseDialogSx}>
    <DialogTitle><Box><Typography variant="h5" sx={{ fontWeight: 700 }}>{value?.profile?.displayName || value?.profile?.profileName}</Typography><Typography variant="body2" color="text.secondary">{currentLocale === "en" ? "Traffic analytics for the selected period" : "Аналитика трафика за выбранный период"}</Typography></Box></DialogTitle>
    <DialogContent>{value?.loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Box> : !data?.enabled ? <Box sx={{ py: 6, textAlign: "center" }}><Typography>{currentLocale === "en" ? "Analytics collection is disabled in administration settings." : "Сбор аналитики выключен в настройках администрирования."}</Typography></Box> : <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
        <Typography sx={{ fontWeight: 650, mb: 1 }}>{currentLocale === "en" ? "Current connection summary" : "Сводка о текущем подключении"}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap flexWrap="wrap">
          <MetaPill icon={<PublicRoundedIcon sx={{ fontSize: 18 }} />} label="IP" value={value?.profile?.externalIp || "—"} />
          <MetaPill icon={<PlaceRoundedIcon sx={{ fontSize: 18 }} />} label={currentLocale === "en" ? "Approximate location" : "Примерное местоположение"} value={renderLocation(value?.profile) || "—"} />
          <MetaPill icon={<LanRoundedIcon sx={{ fontSize: 18 }} />} label={currentLocale === "en" ? "Likely provider" : "Вероятный провайдер"} value={[value?.profile?.provider, value?.profile?.asn].filter(Boolean).join(" · ") || "—"} />
          <MetaPill icon={<LanRoundedIcon sx={{ fontSize: 18 }} />} label={currentLocale === "en" ? "Network prefix" : "Сетевой префикс"} value={profile.networkPrefix || "—"} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>{currentLocale === "en" ? "Provider data sources" : "Источники данных о провайдере"}: {profile.ipDataSources || "—"}</Typography>
      </Paper>
      <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
        <Typography sx={{ fontWeight: 650, mb: 1 }}>{currentLocale === "en" ? "WireGuard connection details" : "Технические данные WireGuard"}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap flexWrap="wrap">
          <MetaPill icon={<PublicRoundedIcon sx={{ fontSize: 18 }} />} label={currentLocale === "en" ? "Active protocol" : "Активный протокол"} value={transportLabel} />
          <MetaPill icon={<HistoryRoundedIcon sx={{ fontSize: 18 }} />} label={currentLocale === "en" ? "Last handshake" : "Последнее рукопожатие"} value={handshakeLabel} />
          <MetaPill icon={<LanRoundedIcon sx={{ fontSize: 18 }} />} label={currentLocale === "en" ? "Source port" : "Исходный порт"} value={endpointPort || "—"} />
          <MetaPill icon={<LanRoundedIcon sx={{ fontSize: 18 }} />} label="Allowed IPs" value={profile.allowedIps || "—"} />
          <MetaPill icon={<HistoryRoundedIcon sx={{ fontSize: 18 }} />} label="Persistent keepalive" value={profile.persistentKeepalive && profile.persistentKeepalive !== "off" ? `${profile.persistentKeepalive} s` : (currentLocale === "en" ? "Disabled" : "Выключен")} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.25 }}>{currentLocale === "en" ? "WireGuard does not transmit the device model, operating system, device name, or client version to the server." : "WireGuard не передаёт серверу модель устройства, операционную систему, имя устройства или версию клиента."}</Typography>
      </Paper>
      <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" }, pt: 0.75 }}><AnalyticsPeriodSelect value={value?.periodHours || "all"} currentLocale={currentLocale} onChange={(period) => onPeriodChange?.(value?.profile, period)} disabled={!!value?.loading} /></Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>{[[currentLocale === "en" ? "Total" : "Всего", formatBytes(totalTraffic), "#6366f1"], [currentLocale === "en" ? "Downloaded" : "Скачано", formatBytes(downloadedTotal), "#06b6d4"], [currentLocale === "en" ? "Uploaded" : "Отправлено", formatBytes(uploadedTotal), "#22c55e"], [currentLocale === "en" ? "Average data rate" : "Средняя скорость передачи", formatCompactBitrate(averageBitrate), "#f59e0b"]].map(([label, displayValue, color], index) => <Paper component={motion.div} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.07, duration: 0.32 }} key={label} variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 3, borderTop: `3px solid ${color}` }}><Typography variant="body2" color="text.secondary">{label}</Typography><Typography variant="h6" sx={{ fontWeight: 700 }}>{displayValue}</Typography></Paper>)}</Stack>
      <Paper component={motion.div} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.36 }} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{currentLocale === "en" ? "Traffic dynamics" : "Динамика трафика"}</Typography>
        <Stack direction="row" spacing={{ xs: 1.25, sm: 2 }} sx={{ mb: 2, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary"><Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: 1, bgcolor: "#06b6d4", mr: 0.6 }} />{currentLocale === "en" ? "Downloaded" : "Скачано"}</Typography>
          <Typography variant="caption" color="text.secondary"><Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: 1, bgcolor: "#22c55e", mr: 0.6 }} />{currentLocale === "en" ? "Uploaded" : "Отправлено"}</Typography>
        </Stack>
        <Box sx={{ height: { xs: 150, sm: 190 }, display: "grid", gridTemplateColumns: `repeat(${Math.max(traffic.length, 1)}, minmax(0, 1fr))`, columnGap: "1px", alignItems: "end", overflow: "hidden" }}>
          {traffic.length ? traffic.map((item, index) => {
            const uploaded = Number(item.rxBytes || 0);
            const downloaded = Number(item.txBytes || 0);
            const itemTotal = downloaded + uploaded;
            const height = itemTotal > 0 ? Math.max(3, (itemTotal / maxTraffic) * 100) : 2.5;
            return <Tooltip key={item.at} title={`${new Date(item.at).toLocaleString()} · ${currentLocale === "en" ? "downloaded" : "скачано"}: ${formatBytes(downloaded)} · ${currentLocale === "en" ? "uploaded" : "отправлено"}: ${formatBytes(uploaded)}`}>
              <Box sx={{ width: "100%", minWidth: 0, height: `${height}%` }}>
                <Box component={motion.div} initial={{ scaleY: 0, opacity: 0.35 }} animate={{ scaleY: 1, opacity: 1 }} transition={{ delay: Math.min(index * 0.012, 0.35), duration: 0.42, ease: "easeOut" }} sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", overflow: "hidden", borderRadius: "4px 4px 0 0", transformOrigin: "bottom center" }}>
                  <Box sx={{ height: itemTotal > 0 ? `${((uploaded / itemTotal) * 100)}%` : "100%", bgcolor: "#22c55e" }} />
                  {itemTotal > 0 ? <Box sx={{ height: `${((downloaded / itemTotal) * 100)}%`, bgcolor: "#06b6d4" }} /> : null}
                </Box>
              </Box>
            </Tooltip>;
          }) : <Typography color="text.secondary">{currentLocale === "en" ? "Data will appear as profiles generate traffic." : "Данные появятся после передачи трафика профилями."}</Typography>}
        </Box>
        {timeLabels.length ? <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", mt: 1, pt: 0.75, borderTop: "1px solid", borderColor: "divider" }}>{timeLabels.map((timestamp, index) => <Typography key={`${timestamp}-${index}`} variant="caption" color="text.secondary" sx={{ textAlign: index === 0 ? "left" : index === 2 ? "right" : "center", display: index === 1 ? { xs: "none", sm: "block" } : "block", gridColumn: index === 2 && timeLabels.length === 3 ? 3 : "auto" }}>{formatAxisTime(timestamp)}</Typography>)}</Box> : null}
      </Paper>
    </Stack>}</DialogContent><DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}><Button variant="contained" onClick={onClose} sx={themedPrimaryButtonSx(currentTheme === "dark")}>{currentLocale === "en" ? "Close" : "Закрыть"}</Button></DialogActions>
  </Dialog>;
}

export default function SkeletonContent(props) {
  const {
    showSettings = false,
    awgProfiles = [],
    awgProfilesLoading = false,
    awgProfilesLoadedOnce = false,
    loading = false,
    currentLocale = "ru",
    currentTheme = "light",
    awgCreateDialog,
    awgEditDialog,
    awgDeleteDialog,
    awgEventsDialog,
    awgShareDialog,
    awgAnalyticsDialog,
    awgAnalyticsSummary,
    analyticsDashboardVisible = true,
    onOpenAwgCreateDialog,
    onCloseAwgCreateDialog,
    onOpenAwgEditDialog,
    onCloseAwgEditDialog,
    onOpenAwgDeleteDialog,
    onCloseAwgDeleteDialog,
    onOpenAwgEventsDialog,
    onCloseAwgEventsDialog,
    onAwgCreateFieldChange,
    onAwgEditFieldChange,
    onAwgCreate,
    onAwgEditSave,
    onAwgDelete,
    onAwgToggleEnabled,
    onAwgDownload,
    onAwgShare,
    onAwgShareCopy,
    onAwgShareEmail,
    onCloseAwgShareDialog,
    onOpenAwgAnalyticsDialog,
    onCloseAwgAnalyticsDialog,
    onAwgAnalyticsSummaryPeriodChange,
    onAwgAnalyticsDialogPeriodChange,
  } = props;

  const [profileSearch, setProfileSearch] = useState("");
  const [downloadDialog, setDownloadDialog] = useState({ open: false, profileName: "", configType: "amnezia" });
  const [shareConfigType, setShareConfigType] = useState("amnezia");
  const [copiedIp, setCopiedIp] = useState("");

  if (showSettings) {
    return <NewAdminPanelPage {...props} />;
  }

  const isDark = currentTheme === "dark";
  const sortedProfiles = [...awgProfiles].sort((a, b) => {
    const onlineDiff = Number(!!b?.online) - Number(!!a?.online);
    if (onlineDiff !== 0) return onlineDiff;
    return String(a?.displayName || a?.profileName || "").localeCompare(String(b?.displayName || b?.profileName || ""), currentLocale === "ru" ? "ru" : "en", { sensitivity: "base" });
  });
  const normalizedProfileSearch = profileSearch.trim().toLocaleLowerCase(currentLocale === "ru" ? "ru" : "en");
  const visibleProfiles = normalizedProfileSearch ? sortedProfiles.filter((profile) => `${profile?.displayName || ""} ${profile?.profileName || ""}`.toLocaleLowerCase(currentLocale === "ru" ? "ru" : "en").includes(normalizedProfileSearch)) : sortedProfiles;
  const totals = sortedProfiles.reduce((acc, item) => {
    acc.totalRateBps += Number(item?.totalRateBps) || 0;
    acc.peakTotalRateBps = Math.max(acc.peakTotalRateBps, Number(item?.peakTotalRateBps) || 0);
    return acc;
  }, { totalRateBps: 0, peakTotalRateBps: 0 });

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pb: 3, mt: 2, flex: 1 }}>
      <Stack spacing={2} sx={{ width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      {!awgAnalyticsSummary?.loading && awgAnalyticsSummary?.enabled && awgAnalyticsSummary?.panelVisible !== false && analyticsDashboardVisible ? <Paper elevation={0} sx={{ ...panelSx, width: "100%", p: { xs: 1.5, sm: 2.25 } }}><TrafficOverview data={awgAnalyticsSummary} currentLocale={currentLocale} onPeriodChange={onAwgAnalyticsSummaryPeriodChange} onOpenProfile={(summaryProfile) => onOpenAwgAnalyticsDialog?.(awgProfiles.find((profile) => profile.profileName === summaryProfile.profileName) || summaryProfile, awgAnalyticsSummary?.periodHours || "all")} /></Paper> : null}
      <Paper elevation={0} sx={{ ...panelSx, width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2, flexDirection: { xs: "column", sm: "row" } }}>
          <Box>
            <Typography variant="h6" sx={sectionTitleSx}>{t("shell.profiles", currentLocale)}</Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: "100%", sm: "auto" } }}>
            <TextField size="small" value={profileSearch} onChange={(event) => setProfileSearch(event.target.value)} placeholder={currentLocale === "en" ? "Search profiles" : "Поиск профилей"} inputProps={{ "aria-label": currentLocale === "en" ? "Search profiles by name" : "Поиск профилей по имени" }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} /></InputAdornment> }} sx={{ ...baseFieldSx(isDark), width: { xs: "100%", sm: 270 }, "& .MuiOutlinedInput-root": { borderRadius: 999 } }} />
            <ActionIcon title={t("shell.add", currentLocale)} onClick={onOpenAwgCreateDialog} currentTheme={currentTheme}><AddRoundedIcon sx={{ fontSize: 20 }} /></ActionIcon>
          </Stack>
        </Box>

        <Stack spacing={1.5} sx={{ minHeight: 220 }}>
          {awgProfilesLoading && !awgProfilesLoadedOnce && sortedProfiles.length === 0 ? (
            <Box sx={{ flex: 1, minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={34} />
            </Box>
          ) : awgProfiles.length === 0 ? (
            <Box sx={{ flex: 1, minHeight: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, border: "1px solid rgba(17,24,39,0.08)", borderRadius: 3, p: { xs: 2, sm: 3 }, bgcolor: "background.default", textAlign: "center" }}>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>{t("shell.noProfiles", currentLocale)}</Typography>
              <Button variant="contained" onClick={onOpenAwgCreateDialog} sx={themedPrimaryButtonSx(isDark)}>{t("shell.add", currentLocale)}</Button>
            </Box>
          ) : visibleProfiles.length === 0 ? (
            <Box sx={{ minHeight: 160, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(17,24,39,0.08)", borderRadius: 3, p: 3, bgcolor: "background.default", textAlign: "center" }}><Typography color="text.secondary">{currentLocale === "en" ? "No profiles match your search" : "По вашему запросу профили не найдены"}</Typography></Box>
          ) : visibleProfiles.map((profile) => {
            const trafficTone = getTrafficTone(profile, totals, isDark);
            return (
              <Box
                component={motion.div}
                layout
                transition={{ layout: { duration: 0.35, ease: "easeInOut" } }}
                key={profile.clientPublicKey || profile.profileName}
                onClick={awgAnalyticsSummary?.enabled ? () => onOpenAwgAnalyticsDialog?.(profile, awgAnalyticsSummary?.periodHours || "all") : undefined}
                sx={{ border: "1px solid rgba(17,24,39,0.08)", borderRadius: 3, p: { xs: 1.25, sm: 1.5 }, bgcolor: "background.default", cursor: awgAnalyticsSummary?.enabled ? "pointer" : "default", ...(awgAnalyticsSummary?.enabled ? { "&:hover": { borderColor: "primary.main", boxShadow: "0 8px 24px rgba(15,23,42,0.08)" } } : {}) }}
              >
                <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", lg: "flex-start" }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.1, flexWrap: "wrap", rowGap: 0.75, mb: 1.05 }}>
                      <Typography sx={{ fontWeight: 600, lineHeight: 1.2, wordBreak: "break-word" }}>{profile.displayName || profile.profileName}</Typography>
                      <Chip label={profile.enabled === false ? (currentLocale === "en" ? "Blocked" : "Заблокирован") : profile.online ? t("shell.statusOnline", currentLocale) : t("shell.statusOffline", currentLocale)} size="small" color={profile.enabled === false ? "warning" : profile.online ? "success" : "default"} />
                    </Stack>

                    <Stack direction="row" spacing={0.9} sx={{ flexWrap: "wrap", rowGap: 0.9, alignItems: "center", justifyContent: "flex-start" }}>
                      <MetaPill icon={<PersonRoundedIcon sx={{ fontSize: 18 }} />} label={t("shell.profileName", currentLocale)} value={profile.profileName} />
                      <MetaPill icon={<EmailRoundedIcon sx={{ fontSize: 18 }} />} label={currentLocale === "en" ? "Email" : "Эл. почта"} value={profile.email || "-"} />
                      <MetaPill icon={<LanRoundedIcon sx={{ fontSize: 18 }} />} label={t("shell.ipAddress", currentLocale)} value={<CopyableIp ip={profile.ipAddress} currentLocale={currentLocale} onCopied={setCopiedIp} />} />
                      {profile.online && profile.externalIp ? <MetaPill icon={<PublicRoundedIcon sx={{ fontSize: 18 }} />} label={t("shell.externalIp", currentLocale)} value={<CopyableIp ip={profile.externalIp} currentLocale={currentLocale} onCopied={setCopiedIp} />} /> : null}
                      {profile.online && renderLocation(profile) ? <MetaPill icon={<PlaceRoundedIcon sx={{ fontSize: 18 }} />} label={t("shell.location", currentLocale)} value={renderLocation(profile)} /> : null}
                    </Stack>

                    <Box sx={{ mt: 1.05, display: "inline-flex", alignItems: "center", gap: 0.85, px: 1.1, py: 0.7, borderRadius: 999, bgcolor: trafficTone.bg, border: `1px solid ${trafficTone.border}` }}>
                      <SwapVertRoundedIcon sx={{ fontSize: 18, color: trafficTone.color }} />
                      <Typography variant="body2" sx={{ color: trafficTone.color, fontWeight: 400 }}>{formatCompactBitrate(profile.totalRateBps)}</Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} onClick={(event) => event.stopPropagation()} sx={{ width: { xs: "100%", lg: "auto" }, justifyContent: { xs: "flex-start", lg: "flex-end" }, flexWrap: "wrap" }}>
                    <ActionIcon title={profile.enabled === false ? (currentLocale === "en" ? "Activate profile" : "Активировать профиль") : (currentLocale === "en" ? "Block profile" : "Заблокировать профиль")} onClick={() => onAwgToggleEnabled(profile)} color={profile.enabled === false ? "success" : "warning"} currentTheme={currentTheme}><PowerSettingsNewRoundedIcon sx={{ fontSize: 20 }} /></ActionIcon>
                    <ActionIcon title={t("shell.viewEvents", currentLocale)} onClick={() => onOpenAwgEventsDialog(profile.profileName, awgEventsDialog?.periodHours || 24)} currentTheme={currentTheme}><HistoryRoundedIcon sx={{ fontSize: 20 }} /></ActionIcon>
                    <ActionIcon title={t("shell.downloadProfile", currentLocale)} onClick={() => setDownloadDialog({ open: true, profileName: profile.profileName, configType: "amnezia" })} currentTheme={currentTheme}><DownloadRoundedIcon sx={{ fontSize: 20 }} /></ActionIcon>
                    <ActionIcon title={t("shell.shareProfile", currentLocale)} onClick={() => { setShareConfigType("amnezia"); onAwgShare(profile); }} currentTheme={currentTheme}><IosShareRoundedIcon sx={{ fontSize: 20 }} /></ActionIcon>
                    <ActionIcon title={t("shell.edit", currentLocale)} onClick={() => onOpenAwgEditDialog(profile)} currentTheme={currentTheme}><EditRoundedIcon sx={{ fontSize: 20 }} /></ActionIcon>
                    <ActionIcon title={t("shell.delete", currentLocale)} onClick={() => onOpenAwgDeleteDialog(profile.profileName)} color="danger" currentTheme={currentTheme}><DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} /></ActionIcon>
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Paper>

      </Stack>

      <ProfileDialog open={!!awgCreateDialog?.open} title={t("shell.createProfile", currentLocale)} currentLocale={currentLocale} currentTheme={currentTheme} form={awgCreateDialog || {}} onClose={onCloseAwgCreateDialog} onChange={onAwgCreateFieldChange} onSave={onAwgCreate} loading={loading} isCreate />
      <ProfileDialog open={!!awgEditDialog?.open} title={t("shell.editProfile", currentLocale)} currentLocale={currentLocale} currentTheme={currentTheme} form={awgEditDialog || {}} onClose={onCloseAwgEditDialog} onChange={onAwgEditFieldChange} onSave={onAwgEditSave} loading={loading} />
      <DownloadProfileDialog open={downloadDialog.open} currentLocale={currentLocale} currentTheme={currentTheme} profileName={downloadDialog.profileName} configType={downloadDialog.configType} onConfigTypeChange={(configType) => setDownloadDialog((value) => ({ ...value, configType }))} onClose={() => setDownloadDialog({ open: false, profileName: "", configType: "amnezia" })} onDownload={async () => { await onAwgDownload(downloadDialog.profileName, downloadDialog.configType); setDownloadDialog({ open: false, profileName: "", configType: "amnezia" }); }} loading={loading} />
      <ShareProfileDialog open={!!awgShareDialog?.open} currentLocale={currentLocale} currentTheme={currentTheme} profile={awgShareDialog || {}} configType={shareConfigType} onConfigTypeChange={setShareConfigType} onClose={onCloseAwgShareDialog} onCopy={onAwgShareCopy} onSendEmail={onAwgShareEmail} loading={loading} />
      <DeleteProfileDialog open={!!awgDeleteDialog?.open} currentLocale={currentLocale} currentTheme={currentTheme} profileName={awgDeleteDialog?.profileName || ""} onClose={onCloseAwgDeleteDialog} onConfirm={onAwgDelete} loading={loading} />
      <ProfileEventsDialog open={!!awgEventsDialog?.open} currentLocale={currentLocale} currentTheme={currentTheme} profileName={awgEventsDialog?.profileName || ""} items={awgEventsDialog?.items || []} periodHours={awgEventsDialog?.periodHours || 24} onClose={onCloseAwgEventsDialog} onPeriodChange={(nextPeriod) => onOpenAwgEventsDialog(awgEventsDialog?.profileName, nextPeriod)} />
      <ProfileAnalyticsDialog value={awgAnalyticsDialog} currentLocale={currentLocale} currentTheme={currentTheme} onClose={onCloseAwgAnalyticsDialog} onPeriodChange={onAwgAnalyticsDialogPeriodChange} />
      <Snackbar open={!!copiedIp} autoHideDuration={2200} onClose={() => setCopiedIp("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}><Alert severity="success" variant="filled" onClose={() => setCopiedIp("")} sx={{ width: "100%" }}>{currentLocale === "en" ? "IP address copied" : "IP-адрес скопирован"}</Alert></Snackbar>
    </Box>
  );
}
